import { FormApiMethodsRequestData } from '../../../api/types/types';
import { useDatasetData } from '../../../api/hooks/useDatasetData';
import { apiRequest } from '../../../api/utils/apiRequest';
import { Dataset, Form, FormType } from '../../../types/types';
import { FormTokenNode, ResponseActionHandler } from '../../../types/context';
import { resolveFormApiMethod } from './resolveFormApiMethod';
import { resolveTemplate } from '../../../utils/json/resolveTemplate';
import { getDataFromDatasetRequest } from '../../../api/utils/getDataFromDatasetRequest';
import { wrapRowAsDatasetDataRes } from '../../../api/utils/wrapRowAsDatasetDataRes';

// Fetches specific row data for editing (edit mode)
export const fetchEditRowData = async (
  resolvedForm: Form,
  resolvedDataset: Dataset,
  rowId: string,
  formType: FormType,
  actualToken: FormTokenNode | null,
  sessionToken: string | null,
  fetchData: ReturnType<typeof useDatasetData>['fetch'],
  detailedFetchData: ReturnType<typeof useDatasetData>['detailedFetch'],
  // Forwarded to openForceErrorModal so cancel skips this row
  forceErrorCancelAction?: () => void,
  getResponseActionHandler?: (name: string) => ResponseActionHandler | undefined
) => {
  const resolvedMethod = resolveFormApiMethod(
    resolvedForm.apiMethods,
    'get-item-values'
  );

  if (!resolvedMethod) {
    throw new Error('No method found');
  }

  const tokenRequestData = actualToken?.meta
    ?.requestData as FormApiMethodsRequestData;
  const fetchRequestData: FormApiMethodsRequestData = {
    ...tokenRequestData,
    // Direct-link copy has no hydrated token, so formType would be missing and
    // the get-item-values header template (x-asw-forcopy) would resolve to null.
    // Derive it from the form route as a fallback.
    formType: tokenRequestData?.formType ?? formType,
    sessionId: sessionToken ?? '',
    id: rowId,
  };

  if (typeof resolvedMethod === 'string') {
    const { apiUrl } = await resolveTemplate(
      { apiUrl: resolvedMethod },
      fetchRequestData
    );

    return await fetchData(
      `${apiUrl}/${rowId}`,
      resolvedDataset,
      false,
      undefined,
      undefined,
      forceErrorCancelAction
    );
  }

  // DetailedApiMethod flow — resolve templates with requestData from token
  const row = await apiRequest(
    resolvedMethod,
    fetchRequestData,
    (apiMethod) =>
      getDataFromDatasetRequest(
        () =>
          detailedFetchData(
            apiMethod,
            resolvedDataset,
            false,
            undefined,
            forceErrorCancelAction
          ),
        true
      ),
    getResponseActionHandler
  );

  // Wrap the bare row back into useDatasetDataRes so the shape matches the string
  // path; createRelevantFormData reads rowData.data[0]. (returnFirst stays true so
  // responseActions still run against the single row.)
  return wrapRowAsDatasetDataRes(row, resolvedDataset.datasetId);
};
